/**
 * Property + Room V2-augmented types — V2 admin module.
 *
 * Mirrors the schema after Phase A migrations (lessors_table,
 * properties_v2_fields, rooms_v2_fields). Inlined here so V2 code can
 * compile before src/integrations/supabase/types.ts is regenerated.
 */
import type { Json } from "@/lib/supabase-types";
import type { Lessor } from "./lessor";

export const PROPERTY_TYPES = [
  "WG",
  "House",
  "Apartment building",
  "Studio complex",
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const ROOM_STATUSES = ["occupied", "vacant", "maintenance"] as const;
export type RoomStatus = (typeof ROOM_STATUSES)[number];

export const REVENUE_MODELS = [
  "full_rent",
  "pct_of_rent",
  "flat_fee_per_room",
] as const;
export type RevenueModel = (typeof REVENUE_MODELS)[number];

/**
 * How rent + the CoLiviq management fee flow (COL-225):
 *   - company_collects: tenant → CoLiviq → owner (net of fee).
 *   - owner_collects:   tenant → owner, owner pays CoLiviq the fee.
 */
export const COLLECTION_MODES = ["company_collects", "owner_collects"] as const;
export type CollectionMode = (typeof COLLECTION_MODES)[number];

/** The two bank-account roles in a money flow (derived from collection mode). */
export const BANK_ACCOUNT_ROLES = ["rent_receipt", "fee_payout"] as const;
export type BankAccountRole = (typeof BANK_ACCOUNT_ROLES)[number];

export const PAYOUT_METHODS = ["SEPA", "Überweisung"] as const;
export type PayoutMethod = (typeof PAYOUT_METHODS)[number];

export const APARTMENT_RENTAL_STATUSES = ["vacant", "occupied"] as const;
export type ApartmentRentalStatus = (typeof APARTMENT_RENTAL_STATUSES)[number];

export type RepOverride = {
  name?: string | null;
  role?: string | null;
  note?: string | null;
};

export type LessorOverride = {
  lessor_id?: string | null;
  rep_name?: string | null;
  rep_role?: string | null;
  rep_note?: string | null;
};

export type Property = {
  id: number;
  title: string;
  city: string;
  address: string;
  lat: number;
  lng: number;
  description: string | null;
  description_translations: Record<string, string> | null;
  image_url: string | null;
  images: Json;
  virtual_tour_url: string | null;
  is_hidden: boolean;
  stale_since: string | null;
  storyblok_id: number | null;
  default_contract_template_id: string | null;
  /** Property type (WG / House / …). Present on the main lineage. */
  type: PropertyType | null;
  // V2 fields
  lessor_id: string | null;
  rep_override: RepOverride | null;
  master_rent: number | null;
  lease_start: string | null;
  lease_end: string | null;
  notice_period: string | null;
  /** Property-level revenue model. NULL = not set (defined per apartment). */
  revenue_model: RevenueModel | null;
  coliviq_fee_pct: number | null;
  /** Flat management fee in EUR — used when revenue_model = flat_fee_per_room. */
  coliviq_fee_amount: number | null;
  /** How rent + fee flow. Default for apartments unless they override. */
  collection_mode: CollectionMode;
  /** Whether automated rent reminders/dunning run for this property's units.
   *  Default for apartments unless they override. false = suppress (e.g. owner
   *  collects rent, no bank visibility). */
  rent_reminders_enabled: boolean;
  /** Mahngebühr per dunning stage in EUR (COL-308). NOT NULL, defaults to 5.
   *  Resolve raw values through resolveDunningFee before rendering. */
  dunning_fee_per_stage_eur: number;
  /** Payment methods this property's units accept, resolved room → apartment →
   *  property → platform default by get_room_payment_methods. NULL = the
   *  property states nothing and the platform default applies (card on,
   *  transfer off, direct debit off). Edited in Edit property → Payment
   *  methods. */
  stripe_card_enabled: boolean | null;
  bank_transfer_enabled: boolean | null;
  sepa_debit_enabled: boolean | null;
  /** CoLiviq-held account for this property. NULL = default platform account. */
  platform_bank_account_id: number | null;
  /** Default key pickup location / partner shop name. Inherited by apartments + rooms. */
  keynest_pickup_location: string | null;
  /** Default key pickup street address. Inherited by apartments + rooms. */
  keynest_pickup_address: string | null;
  /** Optional property-level KeyNest link (fallback). Per-room links override it. */
  keynest_access_url: string | null;
  /** Key pickup method: 'keynest' or 'manual'. Inherited by apartments + rooms. */
  pickup_method: PickupMethod | null;
  /** Free-text pickup instructions shown to the tenant (mainly manual pickup). */
  pickup_instructions: string | null;
  // Legacy contract fields (kept for back-compat — V2 prefers lessor)
  leasor_name: string | null;
  representor_name: string | null;
  street_name: string | null;
  street_number: string | null;
  zip_code: string | null;
  city_name: string | null;
  leasor_signature_url: string | null;
  /** Owner self-serve submission workflow (20260813130200). 'approved' for
   *  every pre-existing/staff-created property. */
  approval_status: "pending" | "approved" | "rejected" | "withdrawn";
  submitted_by: string | null;
  submitted_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string;
  updated_at: string;
};

export type Room = {
  id: number;
  property_id: number;
  title: string;
  price: number;
  available: boolean;
  description: string | null;
  description_translations: Record<string, string> | null;
  images: Json;
  is_hidden: boolean;
  // V2 fields
  lessor_override: LessorOverride | null;
  size_sqm: number | null;
  floor: string | null;
  available_from: string | null;
  status: RoomStatus | null;
  sort_order: number | null;
  /** The apartment (owner-owned unit) this room belongs to. NEW layer. */
  apartment_id: number | null;
  // Keynest legacy fields
  keynest_pickup_code: string | null;
  keynest_access_code: string | null;
  keynest_partner_shop_name: string | null;
  keynest_partner_shop_address: string | null;
  keynest_locker_number: string | null;
  keynest_opening_hours_weekdays: string | null;
  keynest_opening_hours_saturday: string | null;
  keynest_status_label: string | null;
  keynest_key_label: string | null;
  /** Per-room KeyNest access link (the room's own key). Overrides the
   *  property-level keynest_access_url fallback. */
  keynest_access_url: string | null;
  /** Room-level key pickup method override. NULL = inherit apartment → property. */
  pickup_method: PickupMethod | null;
  /** Room-level pickup instructions override. NULL = inherit apartment → property. */
  pickup_instructions: string | null;
  roommates_count: number | null;
  house_features: Json;
  created_at: string;
  updated_at: string;
};

export type PropertyBankOverride = {
  property_id: number;
  holder: string | null;
  iban: string | null;
  bic: string | null;
  bank_name: string | null;
  created_at: string;
  updated_at: string;
};

export type RoomBankOverride = {
  room_id: number;
  holder: string | null;
  iban: string | null;
  bic: string | null;
  bank_name: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * Apartment — owner-owned unit within a property (Property → Apartment →
 * Room). The owner is a lessor (lessor_id → Lessor) supplying default
 * representor + bank account; commercial terms are columns here.
 */
export type Apartment = {
  id: number;
  property_id: number;
  label: string;
  floor: string | null;
  size_sqm: number | null;
  /** Declared number of rooms in the unit (admin-entered, not derived). */
  num_rooms: number | null;
  /** Admin-set rental status for the whole apartment. */
  rental_status: ApartmentRentalStatus | null;
  // Owner = lessor by default. owner_user_id points at the property owner;
  // lessor_id is an optional override (set in owner settings) for the
  // exception where a separate legal lessor entity applies.
  owner_user_id: string | null;
  lessor_id: string | null;
  /** Per-apartment representor override. NULL / blank name = inherit from the
   *  owner (lessor), then property override, then property lessor. */
  rep_override: RepOverride | null;
  // Commercial terms
  master_rent: number | null;
  lease_end: string | null;
  payout_method: PayoutMethod | null;
  invested_amount: number | null;
  owner_since: string | null;
  // Revenue / collection overrides (NULL = inherit from property)
  collection_mode: CollectionMode | null;
  /** Per-apartment rent-reminder override. NULL = inherit from property. */
  rent_reminders_enabled: boolean | null;
  revenue_model: RevenueModel | null;
  coliviq_fee_pct: number | null;
  coliviq_fee_amount: number | null;
  /** CoLiviq-held account override. NULL = inherit (property → default). */
  platform_bank_account_id: number | null;
  /** Payment-method overrides for this apartment's rooms. NULL = inherit from
   *  the property. Edited in Edit apartment → Payment methods. */
  stripe_card_enabled: boolean | null;
  bank_transfer_enabled: boolean | null;
  sepa_debit_enabled: boolean | null;
  /** Key pickup location override. NULL = inherit from property. */
  keynest_pickup_location: string | null;
  /** Key pickup address override. NULL = inherit from property. */
  keynest_pickup_address: string | null;
  /** Pickup method override. NULL = inherit from property. */
  pickup_method: PickupMethod | null;
  /** Pickup instructions override. NULL = inherit from property. */
  pickup_instructions: string | null;
  /** Virtual tour URL override. NULL = inherit from property. */
  virtual_tour_url: string | null;
  sort_order: number | null;
  created_at: string;
  updated_at: string;
};

export type ApartmentBankOverride = {
  apartment_id: number;
  holder: string | null;
  iban: string | null;
  bic: string | null;
  bank_name: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * Effective representor / bank for a property or room — resolved from
 * the inheritance chain in the app layer (room override > property
 * override > lessor default).
 */
export type EffectiveRep = {
  name: string | null;
  role: string | null;
  note: string | null;
  source: "room" | "apartment" | "property" | "lessor" | "none";
};

export type EffectiveBank = {
  holder: string | null;
  iban: string | null;
  bic: string | null;
  bank_name: string | null;
  source: "room" | "apartment" | "property" | "lessor" | "platform" | "none";
};

/** How tenants collect keys: a KeyNest partner shop, or a manual/other point. */
export type PickupMethod = "keynest" | "manual";
export const PICKUP_METHODS: PickupMethod[] = ["keynest", "manual"];

/**
 * Resolved key pickup after walking the inheritance chain
 * room override → apartment override → property default. The KeyNest access
 * link/code is NOT part of this — it stays per-room. `method` and
 * `instructions` resolve apartment override → property default.
 */
export type EffectiveKeyPickup = {
  method: PickupMethod;
  location: string | null;
  address: string | null;
  instructions: string | null;
  source: "room" | "apartment" | "property" | "none";
};

/** A CoLiviq-held bank account (admin-managed, selectable for money flows). */
export type PlatformBankAccount = {
  id: number;
  label: string;
  holder: string;
  iban: string;
  bic: string | null;
  bank_name: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
};

/**
 * Resolved revenue/collection config for an apartment, after inheriting any
 * unset field from its property. `source` reports, per field, whether the
 * value came from the apartment override or the property default.
 */
export type EffectiveRevenue = {
  collection_mode: CollectionMode;
  revenue_model: RevenueModel;
  coliviq_fee_pct: number | null;
  coliviq_fee_amount: number | null;
  source: {
    collection_mode: "apartment" | "property";
    revenue_model: "apartment" | "property";
    fee: "apartment" | "property";
  };
};

/** Used in lists where we want a bit of derived data alongside the raw row. */
export type PropertyListItem = Property & {
  rooms_count: number;
  occupied_count: number;
  /** Number of owner-owned apartments grouped under this property. */
  apartments_count: number;
  /**
   * Distinct lessor ids owning apartments in this property. Most buildings
   * carry no property-level `lessor_id` because ownership sits on the
   * individual apartments, so this is what the card falls back to when naming
   * the owner. Empty for non-admins (apartments are admin-only under RLS).
   */
  apartment_lessor_ids: string[];
  /**
   * Display names of the owner ACCOUNTS on this property's apartments, for the
   * card's owner label when no lessor is set anywhere.
   *
   * Not the same question as `apartment_lessor_ids`. A lessor is a billing
   * entity that staff create; `apartments.owner_user_id` is the account that
   * holds the unit. An owner-submitted property has the second and not the
   * first — which is why its card used to read "No owner" with the submitter
   * recorded on every one of its apartments.
   */
  apartment_owner_names: string[];
  /**
   * True when the property has at least one apartment and every one of them
   * is hidden (COL-463). An apartment counts as hidden when every one of its
   * rooms is hidden, including an apartment with zero rooms (vacuous truth —
   * nothing published yet counts as nothing to show). A property with no
   * apartments at all (e.g. a plain WG building) is never true here.
   */
  all_apartments_hidden: boolean;
  /** First non-null URL we can render as the card image. */
  cover_image: string | null;
};

/** Apartment row + derived room/occupancy stats for cards and lists. */
export type ApartmentListItem = Apartment & {
  rooms_count: number;
  occupied_count: number;
  /** Sum of room prices for the apartment (monthly room revenue). */
  rooms_revenue: number;
  /** Ids of every non-hidden room, for per-apartment live-rent lookups. */
  room_ids: number[];
};

/** Re-export Lessor so v2 modules don't need two imports. */
export type { Lessor };
